import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createDebouncedAction } from "@/utils/debounce";

export const DEFAULT_SEARCH_COMMIT_DELAY_MS = 300;

export type SearchDraft = {
  // 입력란에 보이는 값이다. 커밋(URL·소스)과 분리해서 타이핑마다 요청을 보내지 않게 한다.
  draft: string;
  // 타이핑 처리다. 초안을 바꾸고 delayMs 뒤에 커밋을 예약한다. 비우면 즉시 커밋한다.
  change: (next: string) => void;
  // Enter·돋보기 처리다. 대기 중인 예약을 취소하고 지금 값을 바로 커밋한다.
  commitNow: (value?: string) => void;
  // 초안을 버린다(커밋 없이 비움). 후보 선택처럼 타이핑한 글자가 다른 조건으로 쓰였을 때 호출한다.
  // 부모가 같은 턴에 검색을 비우므로, 여기서 또 커밋하면 그 갱신과 겹친다.
  discard: () => void;
};

/**
 * 검색 입력의 초안과 커밋을 분리한다(rules.md §17. 축 상태는 URL 이 소유한다).
 *
 * 커밋된 값(`committed`)은 부모(URL)가 소유한다. 이 훅은 타이핑 중인 초안만 가진다. 타이핑은
 * debounce 뒤에 커밋하고, Enter·✕ 는 즉시 커밋한다. 커밋이 아닌 경로(칩 ✕·↺ 초기화·뒤로가기)로
 * 부모 값이 바뀌면 초안을 그 값으로 덮어쓴다. 이 훅이 방금 커밋한 값이 돌아온 경우는 덮어쓰지
 * 않는다. 그 사이 사용자가 더 입력한 글자를 잃지 않기 위해서다.
 */
export function useSearchDraft(
  committed: string,
  onCommit: (value: string) => void,
  delayMs: number = DEFAULT_SEARCH_COMMIT_DELAY_MS,
): SearchDraft {
  const [draft, setDraft] = useState(committed);
  const debounce = useMemo(() => createDebouncedAction(delayMs), [delayMs]);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // 이 훅이 마지막으로 커밋한 값이다. 부모에서 같은 값이 돌아오면 외부 변경이 아니다.
  const lastCommittedRef = useRef(committed);

  const commit = useCallback((value: string) => {
    lastCommittedRef.current = value;
    onCommitRef.current(value);
  }, []);

  useEffect(() => {
    if (committed === lastCommittedRef.current) {
      return;
    }
    lastCommittedRef.current = committed;
    debounce.cancel();
    setDraft(committed);
  }, [committed, debounce]);

  useEffect(() => () => debounce.cancel(), [debounce]);

  const change = useCallback(
    (next: string) => {
      setDraft(next);
      if (next === "") {
        // ✕ 와 전체 삭제는 기다릴 필요가 없다. 바로 비운다.
        debounce.cancel();
        commit("");
        return;
      }
      debounce.schedule(() => commit(next));
    },
    [commit, debounce],
  );

  const commitNow = useCallback(
    (value?: string) => {
      debounce.cancel();
      commit(value ?? draftRef.current);
    },
    [commit, debounce],
  );

  const discard = useCallback(() => {
    debounce.cancel();
    lastCommittedRef.current = "";
    setDraft("");
  }, [debounce]);

  return { draft, change, commitNow, discard };
}
